import React from 'react';
import { cn } from "@/lib/utils";
import Link from "next/link";
import { ArrowLeftIcon } from "lucide-react";

const RecursiveMap: React.FC<{ items: any; categoriesProductCount: any }> = React.memo(({ items, categoriesProductCount }) => {
    return (
        <ul className={cn('w-full duration-200 relative flex z-10 flex-wrap justify-between items-center list-none')}>
            {items?.map((item: any, index: any) => (
                <RecursiveItem categoriesProductCount={categoriesProductCount} key={index} item={item} />
            ))}
        </ul>
    );
});
RecursiveMap.displayName = "RecursiveMap";

const RecursiveItem: React.FC<{ item: any; categoriesProductCount: any }> = ({ item, categoriesProductCount }) => {
    if (item?.children && item?.slug !== "gift-card") {
        return (
            <section className={cn('text-sm w-fit group px-1 cursor-pointer')}>
                <Link href={`/category/${item.slug}`} prefetch={false} className="group-hover:text-orange-primary duration-100" aria-label={`Go to ${item.title} category`}>
                    {item.title}
                </Link>
                <div className={cn('absolute w-full left-0 invisible px-2 group-hover:visible')}>
                    <div className={cn('duration-200 opacity-0 group-hover:opacity-100')}>
                        <div className={cn('list-none h-full m-0 p-2 flex flex-wrap')}>
                            <CategorySection categoriesProductCount={categoriesProductCount} subTitle={item.title} item={item} />
                        </div>
                    </div>
                </div>
            </section>
        );
    } else {
        return (
            <>
                <Link href={`/category/${item.slug}`} prefetch={false} className={cn('font-medium p-2 hover:text-orange-primary')} aria-label={`Go to ${item.title} category`}>
                    {item.title}
                </Link>

                <Link href={`/brand`} prefetch={false} className={cn('font-medium p-2 hover:text-orange-primary')} aria-label="Go to brands page">
                    برندها
                </Link>

                <Link href={`https://elanza.com/mag/`} target={"_blank"} prefetch={false} rel="noopener noreferrer" className={cn('font-medium p-2 hover:text-orange-primary')} aria-label="Go to the magazine page">
                    مجله
                </Link>
            </>
        );
    }
};

const CategorySection: React.FC<{ item: any, subTitle: any, categoriesProductCount: any }> = React.memo(({ item, subTitle, categoriesProductCount }) => {
    const numColumns = 5;
    const sortedCategories = [...(item.children || [])].sort((a: any, b: any) => (b.children?.length || 0) - (a.children?.length || 0));
    const totalItems = sortedCategories.reduce((sum: number, category: any) => sum + (category.children?.length || 0), 0);
    const itemsPerColumn = Math.ceil(totalItems / numColumns);
    const columns: any[] = Array.from({ length: numColumns }, () => []);
    const columnItemCounts: number[] = Array(numColumns).fill(0);

    sortedCategories.forEach((category: any) => {
        const categoryItemCount = category.children?.length || 0;
        const targetColumnIndex = columnItemCounts.findIndex(count => count + categoryItemCount <= itemsPerColumn);
        if (targetColumnIndex !== -1) {
            columns[targetColumnIndex].push(category);
            columnItemCounts[targetColumnIndex] += categoryItemCount;
        } else {
            const minColumnIndex = columnItemCounts.indexOf(Math.min(...columnItemCounts));
            columns[minColumnIndex].push(category);
            columnItemCounts[minColumnIndex] += categoryItemCount;
        }
    });

    return (
        <div className={cn('w-full bg-white origin-right  mt-1 h-[55vh] max-h-[550px] rounded-b-2xl shadow scroll-custom overflow-auto flex flex-wrap content-start')}>
            <div className="w-full flex flex-wrap text-orange-primary hover:text-orange-hover duration-100 items-center gap-2 p-2">
                <Link href={`/category/${item.slug}`} prefetch={false} as={`/category/${item.slug}`} aria-label={`Go to ${subTitle} category`}>
                    {subTitle}
                </Link>
                <ArrowLeftIcon className={"w-4 h-4"} />
            </div>
            {columns?.map((column, colIndex) => (
                <div key={colIndex} className={cn('w-1/5 border-x  border-gray-50')}>
                    {column.map((category: any, categoryIndex: any) => (
                        <div key={categoryIndex} className={cn("p-2 h-fit flex flex-col")}>
                            <div className={cn('w-full')}>
                                <div className={cn("w-fit font-medium py-2 border-b-2 border-b-blue-900")}>
                                    <Link href={`/category/${category.slug}`} prefetch={false} as={`/category/${category.slug}`} aria-label={`Go to ${category.title} category`}>
                                        {category?.title}
                                    </Link>
                                </div>
                                <ul className="w-full">
                                    {category?.children?.map((child: any, childIndex: any) => (
                                        <li key={childIndex} className={cn("text-gray-500 py-1")}>
                                            {categoriesProductCount[child.id] > 0 && (
                                                <Link href={`/category/${child.slug}`} prefetch={false} as={`/category/${child.slug}`} aria-label={`Go to ${child.title} category`}>
                                                    {child.title}
                                                </Link>
                                            )}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        </div>
                    ))}
                </div>
            ))}
        </div>
    );
});
CategorySection.displayName = "CategorySection";
function MegaMenu({ data, categoriesProductCount }: { data: any, categoriesProductCount: any }) {
    return (
        <section className={cn('w-full flex flex-wrap rounded')}>
            <RecursiveMap categoriesProductCount={categoriesProductCount} items={data} />
        </section>
    );
}

export default MegaMenu;
